import { Fragment } from "react";
import { VisualGroupHeading } from "../VisualGroupHeading";
import { groupHeading } from "../visualGroups";
import { formatCopy } from "../../theme/copy";
import { Box, Typography, type SxProps } from "@mui/material";
import {
  ActionButton,
  ChoiceControl,
  CommonImageComp,
  Hints,
  adaptiveTextGroups,
} from "../../components/ui";
import { optionImageStyle } from "../../components/ui/AutoCompleteDropdown/style";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import { buttonWrapper } from "./buttonStyles";
import type { RankSortDefinition, RankSortDraft, RankSortEvent } from "./core";
import { canToggleRankSortOption } from "./core";
import { rankBoxContainer, valueStyle } from "./styles";

type RankSortViewProps = QuestionViewProps<
  RankSortDefinition,
  RankSortDraft,
  RankSortEvent
>;

const RankSortView = (props: RankSortViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const primaryColor = theme.tokens.color.action.primary;
  const { definition, draft } = props;
  const compact = props.display.arrangement === "compact";
  const list = props.display.view === "list";
  const picked = new Set(draft.orderedIds);

  const choice = (
    option: RankSortDefinition["options"][number],
    index: number,
  ) => {
    const disabled =
      !props.controls.canSubmit ||
      !canToggleRankSortOption(definition, draft, option.id);
    const selected = picked.has(option.id);
    const currentVal = {
      text: option.label,
      image: option.image,
      display_text: option.displayLabel,
    };
    const onClick = () =>
      props.dispatch({ type: "toggle", optionId: option.id });
    return list ? (
      <ChoiceControl
        key={option.id}
        currentVal={currentVal}
        adaptiveText={{ maxLines: 3 }}
        controlAriaLabel={option.label}
        controlName="rank-sort"
        controlValue={String(option.id)}
        disabled={disabled}
        isSelected={selected}
        onClick={onClick}
        buttonImageStyle={optionImageStyle}
        testId={`chip-${index + 1}`}
      />
    ) : (
      <ActionButton
        key={option.id}
        currentVal={currentVal}
        adaptiveText={{ maxLines: 3 }}
        ariaLabel={option.label}
        ariaPressed={selected}
        sx={{ textAlign: "start" }}
        disabled={disabled}
        isSelected={selected}
        onClick={onClick}
        buttonImageStyle={{
          maxHeight: "20vh",
          maxWidth: "100%",
          objectFit: "contain",
        }}
        testId={`chip-${index + 1}`}
      />
    );
  };

  return (
    <Box
      data-testid="rank-sort-view"
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: compact
          ? "minmax(0, 1fr)"
          : "repeat(auto-fit, minmax(min(100%, 24rem), 1fr))",
        gap: `${theme.tokens.spacing["4"]}px`,
      }}
    >
      <Box sx={rankBoxContainer(theme, !compact)}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: `${theme.tokens.spacing["4"]}px`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography sx={{ color: primaryColor }}>
              {definition.labels[1]}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderLeft: "15px solid transparent",
                  borderRight: "15px solid transparent",
                  borderBottom: `25px solid ${primaryColor}`,
                }}
              />
              <Box
                sx={{ width: 15, height: 100, backgroundColor: primaryColor }}
              />
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderLeft: "15px solid transparent",
                  borderRight: "15px solid transparent",
                  borderTop: `25px solid ${primaryColor}`,
                }}
              />
            </Box>
            <Typography sx={{ color: primaryColor }}>
              {definition.labels[0]}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              gap: `${theme.tokens.spacing["3"]}px`,
            }}
          >
            {Array.from({ length: definition.maxOptions }, (_, index) => {
              const option = definition.options.find(
                (item) => item.id === draft.orderedIds[index],
              );
              return (
                <Box
                  key={index}
                  data-testid={`rank-slot-${index}`}
                  sx={
                    [
                      valueStyle(theme),
                      {
                        display: "grid",
                        gridTemplateColumns: "auto minmax(0, 1fr) auto auto",
                        alignItems: "start",
                        gap: `${theme.tokens.spacing["2"]}px`,
                        fontSize: theme.tokens.typography.size.sm,
                        lineHeight: theme.tokens.typography.lineHeight.normal,
                      },
                    ] as SxProps
                  }
                >
                  <Hints
                    text={`${index + 1}.`}
                    variant="label"
                    adaptiveText={{ group: adaptiveTextGroups.rankSlots }}
                    sx={{
                      color: "inherit",
                      textAlign: "start",
                      minWidth: "2ch",
                    }}
                  />
                  {option ? (
                    <>
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          gridColumn: compact ? "2 / -1" : 2,
                          gridRow: 1,
                          "& > div": { alignItems: "flex-start" },
                        }}
                      >
                        <CommonImageComp
                          currentVal={{
                            text: option.label,
                            display_text: option.displayLabel,
                            image: option.image,
                            alt_text: option.label,
                          }}
                          imageStyle={{ maxHeight: "5vh" }}
                          layout="inline"
                          imagePosition="before"
                          adaptiveText={{
                            group: adaptiveTextGroups.rankSlots,
                            maxLines: 2,
                          }}
                          sx={{ textAlign: "start", "& p": { marginBlock: 0 } }}
                        />
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        sx={{ gridColumn: 3, gridRow: compact ? 2 : 1 }}
                        aria-label={formatCopy(theme.copy.actions.moveUp, {
                          option: option.label,
                        })}
                        disabled={index === 0 || !props.controls.canSubmit}
                        onClick={() =>
                          props.dispatch({
                            type: "move",
                            from: index,
                            to: index - 1,
                          })
                        }
                      >
                        ↑
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        sx={{ gridColumn: 4, gridRow: compact ? 2 : 1 }}
                        aria-label={formatCopy(theme.copy.actions.moveDown, {
                          option: option.label,
                        })}
                        disabled={
                          index === draft.orderedIds.length - 1 ||
                          !props.controls.canSubmit
                        }
                        onClick={() =>
                          props.dispatch({
                            type: "move",
                            from: index,
                            to: index + 1,
                          })
                        }
                      >
                        ↓
                      </Box>
                    </>
                  ) : (
                    <Box>...</Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      <Box sx={buttonWrapper(!compact, theme, list)}>
        {definition.options.map((option, index) => (
          <Fragment key={option.id}>
            <VisualGroupHeading
              text={groupHeading(definition.optionGroups, option.id)}
            />
            {choice(option, index)}
          </Fragment>
        ))}
        {definition.dontKnow && (
          <ActionButton
            currentVal={{ text: definition.dontKnow.label, display_text: true }}
            adaptiveText={{ maxLines: 3 }}
            ariaLabel={definition.dontKnow.label}
            disabled={!props.controls.canSubmit}
            isSelected={draft.dontKnow}
            sx={{ textAlign: "start" }}
            onClick={() => props.submit({ type: "dont-know" })}
            testId="chip-dont-know"
          />
        )}
      </Box>
    </Box>
  );
};

export const RankSortButtonsView = RankSortView;
export const RankSortListView = RankSortView;
